import React, { useCallback, useEffect, useId, useRef, useState } from 'react'
import { ChevronDown, SlidersHorizontal, X } from 'lucide-react'
import styles from './FilterPanel.module.scss'
import ModalOverlay from '../../UI/ModalOverlay/ModalOverlay.tsx'

const cx = (...classes: Array<string | false | null | undefined>) => classes.filter(Boolean).join(' ')

interface FilterPanelProps {
  title: string
  filterCount: number
  onReset: () => void
  onToggleAdvanced: () => void
  advancedOpen: boolean
  summary?: React.ReactNode
  actions?: React.ReactNode
  primary: React.ReactNode
  advanced?: React.ReactNode
}

interface FilterSectionProps {
  children: React.ReactNode
  className?: string
}

interface FieldButtonProps {
  title: string
  value?: React.ReactNode
  placeholder?: string
  onClick: () => void
  active?: boolean
  icon?: React.ReactNode
}

interface FieldContainerProps {
  children: React.ReactNode
  className?: string
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  title,
  filterCount,
  onReset,
  onToggleAdvanced,
  advancedOpen,
  summary,
  actions,
  primary,
  advanced,
}) => {
  const advancedButtonRef = useRef<HTMLButtonElement | null>(null)
  const advancedPanelId = useId()
  const [advancedCoords, setAdvancedCoords] = useState<{ top: number; right: number; mode: 'popover' | 'sheet' }>({
    top: 0,
    right: 16,
    mode: 'popover',
  })

  const updateAdvancedPosition = useCallback(() => {
    if (!advancedButtonRef.current) return
    const rect = advancedButtonRef.current.getBoundingClientRect()
    if (window.innerWidth <= 768) {
      setAdvancedCoords((prev) => ({ top: prev.top, right: prev.right, mode: 'sheet' }))
      return
    }

    const margin = 12
    const right = Math.max(window.innerWidth - rect.right - 16, 16)
    setAdvancedCoords({ top: rect.bottom + margin, right, mode: 'popover' })
  }, [])

  useEffect(() => {
    if (!advancedOpen) return
    updateAdvancedPosition()

    window.addEventListener('resize', updateAdvancedPosition)
    window.addEventListener('scroll', updateAdvancedPosition, true)

    return () => {
      window.removeEventListener('resize', updateAdvancedPosition)
      window.removeEventListener('scroll', updateAdvancedPosition, true)
    }
  }, [advancedOpen, updateAdvancedPosition])

  const advancedStyle =
    advancedCoords.mode === 'popover'
      ? ({
          '--advanced-top': `${advancedCoords.top}px`,
          '--advanced-right': `${advancedCoords.right}px`,
        } as React.CSSProperties)
      : undefined

  return (
    <section className={styles.panel}>
      <header className={styles.header}>
        <div className={styles.heading}>
          <h2 className={styles.title}>{title}</h2>
          {filterCount > 0 && <span className={styles.badge}>{filterCount}</span>}
        </div>
        <div className={styles.controls}>
          {actions}
          <button type="button" className={styles.resetButton} onClick={onReset}>
            Limpiar filtros
          </button>
          {advanced && (
            <div className={styles.advancedSlot}>
              <button
                type="button"
                className={cx(styles.advancedButton, advancedOpen && styles.advancedButtonActive)}
                onClick={onToggleAdvanced}
                aria-expanded={advancedOpen}
                aria-controls={advancedPanelId}
                ref={advancedButtonRef}
              >
                <SlidersHorizontal size={16} />
                Avanzada
                <ChevronDown size={18} />
              </button>
            </div>
          )}
        </div>
      </header>
      {advanced && (
        <ModalOverlay isOpen={advancedOpen} onClose={onToggleAdvanced} zIndex={30}>
          <div className={styles.advancedLayer}>
            <div
              id={advancedPanelId}
              role="dialog"
              aria-modal="true"
              aria-hidden={!advancedOpen}
              className={cx(
                styles.advancedPopover,
                advancedOpen && styles.advancedPopoverOpen,
                advancedCoords.mode === 'sheet' && styles.advancedPopoverSheet,
              )}
              style={advancedStyle}
            >
              <div className={styles.advancedHeader}>
                <span className={styles.advancedTitle}>Filtros avanzados</span>
                <button type="button" className={styles.advancedClose} onClick={onToggleAdvanced} aria-label="Cerrar filtros avanzados">
                  <X size={16} />
                </button>
              </div>
              <div className={styles.advancedContent}>{advanced}</div>
            </div>
          </div>
        </ModalOverlay>
      )}
      <div className={styles.sections}>{primary}</div>
      {summary && <div className={styles.summary}>{summary}</div>}
    </section>
  )}

const FilterField: React.FC<FieldContainerProps> = ({ children, className }) => {
  return <div className={cx(styles.fieldControl, className)}>{children}</div>
}

const FilterSection: React.FC<FilterSectionProps> = ({ children, className }) => {
  return <div className={cx(styles.section, className)}>{children}</div>
}

const FilterFieldButton: React.FC<FieldButtonProps> = ({ title, value, placeholder, onClick, active, icon }) => {
  return (
    <button
      type="button"
      className={cx(styles.fieldButton, active && styles.fieldButtonActive)}
      onClick={onClick}
      aria-expanded={active}
    >
      <span className={styles.fieldButtonLabel}>
        <span className={styles.fieldButtonTitle}>{title}</span>
        <span className={cx(styles.fieldButtonValue, !value && styles.fieldButtonPlaceholder)}>
          {value || placeholder || 'Seleccionar'}
        </span>
      </span>
      <span className={styles.fieldButtonIcon}>{icon || <ChevronDown size={16} />}</span>
    </button>
  )}

const FilterDropdown: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className={styles.dropdown}>
      <div className={styles.dropdownList}>{children}</div>
    </div>
  )}

const FilterDropdownItem: React.FC<{
  onSelect: () => void
  active?: boolean
  children: React.ReactNode
}> = ({ onSelect, active, children }) => {
  return (
    <div className={cx(styles.dropdownItem, active && styles.dropdownItemActive)} onClick={onSelect}>
      {children}
    </div>
  )}

export { FilterPanel, FilterSection, FilterField, FilterFieldButton, FilterDropdown, FilterDropdownItem }

